import React from 'react';
import { Typography, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { formatDescription, getCurrencySymbol, formatPercentageValue } from '../utils/formatters';
import { getMetricColorStyle } from '../utils/colorUtils';

const { Text } = Typography;

/**
 * Aylık metrik kartları bileşeni
 */
const MonthlyMetrics = ({ brandId, allMetricsData }) => {
  if (!allMetricsData[brandId] || !allMetricsData[brandId].filteredMetrics || allMetricsData[brandId].filteredMetrics.length === 0) {
    return <Text type="secondary">Bu marka için aylık metrik verisi bulunamadı.</Text>;
  }

  // Marka bilgisini al
  const brandInfo = allMetricsData[brandId]?.brandInfo || { currency: 'TRY' };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-2 sm:gap-3 md:gap-4">
      {allMetricsData[brandId].filteredMetrics.map(metric => {
        // Metrik için renk stilini hesapla
        const colorStyle = getMetricColorStyle(metric.id, metric.value, allMetricsData, brandId);
        
        // Border stillerini hesapla
        const getBorderStyle = () => {
          // 10009 ID'li metrik için ROI karşılaştırması (GUNCEL_ROI vs HEDEF_ROI)
          if (metric.id === 10009) {
            const compareMetric = allMetricsData[brandId]?.allMetrics.find(m => m.id === 10008);
            if (compareMetric) {
              const currentValue = parseFloat(metric.value);
              const compareValue = parseFloat(compareMetric.value);
              return {
                border: currentValue < compareValue ? '2px solid rgba(107, 114, 128, 0.7)' : '2px solid #22c55e'
              };
            }
          }
          
          // 10063 ID'li metrik için 10055 ile karşılaştırma (TRENDYOL_GUNCEL_ROI vs TRENDYOL_HEDEF_ROI)
          if (metric.id === 10063) {
            const compareMetric = allMetricsData[brandId]?.allMetrics.find(m => m.id === 10055);
            if (compareMetric) {
              const currentValue = parseFloat(metric.value);
              const compareValue = parseFloat(compareMetric.value);
              return {
                border: currentValue < compareValue ? '2px solid rgba(107, 114, 128, 0.7)' : '2px solid #22c55e'
              };
            }
          }
          
          // Hedef sapması metrikler için negatif/pozitif kontrol
          if ([10010, 10062, 10012, 10064, 10013, 10048].includes(metric.id)) {
            const value = parseFloat(metric.value);
            return {
              border: value < 0 ? '2px solid rgba(107, 114, 128, 0.7)' : '2px solid #22c55e'
            };
          }
          
          return { border: '1px solid #d1d5db' };
        };
        
        return (
          <div 
            key={metric.id} 
            className="
              bg-white rounded-lg p-2 sm:p-3 
              shadow-sm hover:shadow-md transition-shadow duration-200
              flex flex-col justify-between min-h-[100px] sm:min-h-[120px]
            "
            style={{ 
              backgroundColor: metric.color, 
              opacity: 0.9,
              ...getBorderStyle()
            }}
          >
            {/* Metrik Başlığı */}
            <div className="flex justify-between items-start mb-2">
              <span className="text-[10px] sm:text-xs font-bold text-gray-900 leading-tight break-words">
                {formatDescription(metric.description || metric.name)}
              </span>
              <Tooltip title={metric.internalNotes || (metric.isAutoCalculated ? "Otomatik Hesaplanan" : "Manuel")}>
                <InfoCircleOutlined className="text-yellow-500 text-xs flex-shrink-0 ml-1" />
              </Tooltip>
            </div>
            
            {/* Metrik Değeri */}
            <div 
              className={`
                text-sm sm:text-base text-gray-700 bg-gray-50 
                p-2 rounded border border-gray-200 font-medium
                ${getValueBackgroundClass(metric, allMetricsData[brandId])}
              `}
              style={{
                ...colorStyle,
                ...getValueBackgroundStyle(metric, allMetricsData[brandId])
              }}
            >
              {renderMetricValue(metric, brandInfo.currency)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Değer arka plan rengini hesaplar
 */
const getValueBackgroundClass = (metric, brandData) => {
  // 10009 ID'li metrik için karşılaştırma
  if (metric.id === 10009) {
    const compareMetric = brandData?.allMetrics.find(m => m.id === 10008);
    if (compareMetric) {
      const currentValue = parseFloat(metric.value);
      const compareValue = parseFloat(compareMetric.value);
      return currentValue < compareValue ? 'text-white font-bold' : 'text-white font-bold';
    }
  }
  
  // 10063 ID'li metrik için 10055 ile karşılaştırma
  if (metric.id === 10063) {
    const compareMetric = brandData?.allMetrics.find(m => m.id === 10055);
    if (compareMetric) {
      const currentValue = parseFloat(metric.value);
      const compareValue = parseFloat(compareMetric.value);
      return currentValue < compareValue ? 'text-white font-bold' : 'text-white font-bold';
    }
  }
  
  return '';
};

/**
 * Değer arka plan stilini hesaplar
 */
const getValueBackgroundStyle = (metric, brandData) => {
  // 10009 ID'li metrik için karşılaştırma
  if (metric.id === 10009) {
    const compareMetric = brandData?.allMetrics.find(m => m.id === 10008);
    if (compareMetric) {
      const currentValue = parseFloat(metric.value);
      const compareValue = parseFloat(compareMetric.value);
      return currentValue < compareValue ? 
        { backgroundColor: 'rgba(107, 114, 128, 0.7)' } : 
        { backgroundColor: '#22c55e' };
    }
  }
  
  // 10063 ID'li metrik için 10055 ile karşılaştırma
  if (metric.id === 10063) {
    const compareMetric = brandData?.allMetrics.find(m => m.id === 10055);
    if (compareMetric) {
      const currentValue = parseFloat(metric.value);
      const compareValue = parseFloat(compareMetric.value);
      return currentValue < compareValue ? 
        { backgroundColor: 'rgba(107, 114, 128, 0.7)' } : 
        { backgroundColor: '#22c55e' };
    }
  }
  
  return {};
};

/**
 * Metrik değerini biçimlendirir
 */
const renderMetricValue = (metric, currency = 'TRY') => {
  if (metric.value === null || metric.value === undefined) {
    return '-';
  }
  
  if (metric.dataType === 'text') {
    const text = String(metric.value);
    // Mobile için daha kısa kesilim
    const maxLength = window.innerWidth < 640 ? 20 : 42;
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }
  
  if (metric.dataType === 'percentage') {
    return formatPercentageValue(metric.value);
  }
  
  if (metric.dataType === 'currency') {
    // Para birimi için kuruşları kaldır (tam sayıya yuvarla)
    const value = Math.round(parseFloat(metric.value));
    // Mobile için kısaltılmış format
    if (window.innerWidth < 640 && value > 1000000) {
      return (value / 1000000).toFixed(1) + 'M ' + getCurrencySymbol(currency);
    } else if (window.innerWidth < 640 && value > 1000) {
      return (value / 1000).toFixed(0) + 'K ' + getCurrencySymbol(currency);
    }
    return value.toLocaleString('tr-TR') + ' ' + getCurrencySymbol(currency);
  }
  
  return parseFloat(metric.value).toLocaleString('tr-TR');
};

export default MonthlyMetrics; 